import { KeyboardEvent, MouseEvent, ReactElement, useRef, useState } from 'react';
import { endOfWeek, format, isValid, startOfWeek } from 'date-fns';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popover from '@mui/material/Popover';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { ReactComponent as CalendarIcon } from '~/ui/assets/images/calendarIcon.svg';
import { isPickerOpenKey, useRestoredFocusRing } from '~/ui/components/inputs/PickerDraft';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './DatePicker.module.scss';

export type WeekRange = [Date, Date];

interface IProps {
  size?: FormControlProps['size'];
  label?: string;
  error?: string;
  value: WeekRange | null;
  onChange: (range: WeekRange) => void;
  /** Override displayed value (e.g. "Nov - Dec - Jan") */
  displayValue?: string;
  disabled?: boolean;
  className?: string;
  tabIndex?: number;
  showIcon?: boolean;
  fullWidth?: boolean;
  /** Week starts on: 0 = Sunday, 1 = Monday */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

const formatWeekRange = (range: WeekRange | null): string => {
  if (!range || !isValid(range[0]) || !isValid(range[1])) return '';
  const sameYear = range[0].getFullYear() === range[1].getFullYear();
  const startStr = format(range[0], sameYear ? 'd MMM' : 'd MMM yyyy');
  const endStr = format(range[1], 'd MMM yyyy');
  return `${startStr} - ${endStr}`;
};

const WeekPickerMUI = ({
  size,
  label,
  error,
  value,
  onChange,
  displayValue,
  disabled,
  className,
  tabIndex = 0,
  showIcon = true,
  fullWidth = true,
  weekStartsOn = 0,
}: IProps): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const focusRing = useRestoredFocusRing();
  const isError = Boolean(error);

  const displayText = displayValue ?? formatWeekRange(value);
  const pickerDate = value?.[0] ?? new Date();

  const handleOpen = (e: MouseEvent<HTMLDivElement>) => {
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled || !isPickerOpenKey(e)) return;
    e.preventDefault();
    setAnchorEl(fieldRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
    focusRing.onPopupClose();
  };

  const handleDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    const start = startOfWeek(date, { weekStartsOn });
    const end = endOfWeek(date, { weekStartsOn });
    onChange([start, end]);
    handleClose();
  };

  return (
    <FormControl
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
      focused={focusRing.fieldFocused}
      className={styles.datePickerControl}
    >
      {label && (
        <InputLabel shrink error={isError}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        ref={fieldRef}
        value={displayText}
        readOnly
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        onBlur={focusRing.onFieldBlur}
        error={!!error}
        disabled={disabled}
        slotProps={{ input: { tabIndex } }}
        className={className}
        endAdornment={showIcon ? <CalendarIcon color={variables.colorRed} /> : undefined}
        label={label}
        notched={!!label}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        {...focusRing.popupHandlers}
        slotProps={{
          transition: { timeout: 0 },
          paper: { className: styles.weekPickerPopover },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar value={pickerDate} onChange={handleDateChange} autoFocus />
        </LocalizationProvider>
      </Popover>

      {error && (
        <FormHelperText error className={styles.errorText}>
          {error.toString()}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default WeekPickerMUI;
